import { AlgoliaProductCardRenderer } from '@lyle/algolia-product-card'
import {
  algoliaProductAttributes,
  AlgoliaProductFetcher,
} from '@lyle/algolia-product-fetcher'
import {
  buildAlgoliaFilters,
  clamp,
  escapeAlgoliaFilterValue,
  isPriceAttribute,
  rangeBounds,
  rangePercent,
  rangeStyle,
  rangeTicks,
  resolveFilterOptions,
} from '@lyle/algolia-filter-utils'
import {
  algoliaUserToken,
  createMoneyFormatter,
  escapeHtml,
  formatMoney,
  imageUrl,
  parseJson,
  trackAlgoliaProductClick,
  trackAlgoliaProductConversion,
} from '@lyle/algolia-product-utils'
import { getLocalisedRoute } from '@lyle/weglot'

const ALGOLIA_QUERY_ENDPOINT = appId => {
  return `https://${appId}-dsn.algolia.net/1/indexes/*/queries`
}

const SELECTED_PRODUCT_KEY = 'selected_product_id'
const SELECTED_QUERY_KEY = 'queryId'
const HIDDEN_SALE_COOKIE = 'ls-can-view-hidden-sale'
const CATEGORY_FILTER_ATTRIBUTE = 'metafields.custom.product_categories'
const CATEGORY_PARENT_FILTER_ATTRIBUTE = 'metafields.custom.product_category'
const DRAWER_ANIMATION_MS = 300
const PANEL_ANIMATION_MS = 180

class LyleMainCollection extends HTMLElement {
  #abortController = null
  #cardRenderer = null
  #closingPanel = null
  #config = null
  #drawerAnimationTimer = null
  #fetcher = null
  #hydratedSwatches = new WeakSet()
  #iconMarkup = {}
  #moneyFormatter = null
  #openingPanel = null
  #panelAnimationTimer = null
  #panelOpenAnimationTimer = null
  #quickView = null
  #quickViewPromise = null
  #searchAbortController = null
  #searchSequence = 0
  #state = {
    facets: {},
    openPanel: null,
    page: 0,
    pageSize: 16,
    ranges: {},
    sortIndex: '',
  }
  #lastResponse = null
  #lastFacetResponse = null
  #swatchObserver = null

  connectedCallback() {
    this.#abortController = new AbortController()
    this.#config = this.#parseConfig()

    if (!this.#config) {
      return
    }

    this.#state.pageSize = Number(this.#config.pagination.defaultPageSize) || 16
    this.#state.sortIndex = this.#config.algolia.indexName
    this.#iconMarkup = window.icons || {}
    this.#moneyFormatter = this.#createMoneyFormatter()
    this.#fetcher = new AlgoliaProductFetcher({
      config: this.#config,
      signal: this.#abortController.signal,
    })
    this.#cardRenderer = new AlgoliaProductCardRenderer({
      config: this.#config,
      icons: this.#iconMarkup,
      isMobile: () => this.#isMobile(),
    })
    this.#state = {
      ...this.#state,
      ...this.#stateFromUrl(),
    }

    this.#bindEvents()
    this.#handleHiddenSale()
    this.#syncRelatedLinksActiveState()
    this.#search()
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    this.#searchAbortController?.abort()
    this.#swatchObserver?.disconnect()

    window.clearTimeout(this.#drawerAnimationTimer)
    window.clearTimeout(this.#panelAnimationTimer)
    window.clearTimeout(this.#panelOpenAnimationTimer)
  }

  #parseConfig() {
    const script = this.querySelector('[data-main-collection-config]')

    if (!script?.textContent) {
      return null
    }

    try {
      return JSON.parse(script.textContent)
    } catch {
      return null
    }
  }

  #bindEvents() {
    this.addEventListener('click', this.#handleClick, {
      signal: this.#abortController.signal,
    })
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, {
      signal: this.#abortController.signal,
    })
    this.addEventListener('change', this.#handleChange, {
      signal: this.#abortController.signal,
    })
    this.addEventListener('input', this.#handleInput, {
      signal: this.#abortController.signal,
    })
    this.addEventListener('toggle', this.#handleToggle, {
      signal: this.#abortController.signal,
    })
    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#abortController.signal,
    })
    window.addEventListener('resize', this.#handleResize, {
      signal: this.#abortController.signal,
    })
  }

  #handleClick = event => {
    const relatedLink = event.target.closest('a.related-links__button')
    const panelToggle = event.target.closest('[data-panel-toggle]')
    const facetOption = event.target.closest('[data-facet-value]')
    const rangeApply = event.target.closest('[data-range-apply]')
    const rangeReset = event.target.closest('[data-range-reset]')
    const sortOption = event.target.closest('[data-sort-value]')
    const pageSizeOption = event.target.closest('[data-page-size-value]')
    const activeFilter = event.target.closest('[data-active-filter]')
    const clearAll = event.target.closest(
      '[data-clear-all], .main-collection__drawer-clear'
    )
    const pagination = event.target.closest('[data-page]')
    const mobileFilters = event.target.closest(
      '[data-main-collection-mobile-filters]'
    )
    const drawerClose = event.target.closest(
      '[data-main-collection-drawer-close], .main-collection__drawer-view-results'
    )
    const productLink = event.target.closest('[data-product-link]')
    const quickView = event.target.closest('[data-quick-view]')
    const quickViewClose = event.target.closest('[data-quick-view-close]')
    const quickViewAdd = event.target.closest('[data-quick-view-add]')
    const quickViewArrow = event.target.closest('[data-quick-view-arrow]')
    const quickViewSize = event.target.closest('[data-quick-view-size]')
    const quickViewSwatch = event.target.closest('[data-quick-view-swatch]')
    const swatch = event.target.closest('[data-card-swatch]')

    if (relatedLink && this.#applyRelatedLink(relatedLink)) {
      event.preventDefault()
      return
    }

    if (panelToggle) {
      event.preventDefault()
      this.#togglePanel(panelToggle.dataset.panelToggle)
      return
    }

    if (facetOption) {
      event.preventDefault()
      this.#toggleFacet(
        facetOption.dataset.facetAttribute,
        facetOption.dataset.facetValue
      )
      return
    }

    if (rangeApply) {
      event.preventDefault()
      this.#applyRange(rangeApply.dataset.rangeAttribute)
      return
    }

    if (rangeReset) {
      event.preventDefault()
      this.#clearRange(rangeReset.dataset.rangeReset)
      return
    }

    if (sortOption) {
      event.preventDefault()
      this.#state.sortIndex = sortOption.dataset.sortValue
      this.#state.page = 0
      this.#closePanels()
      this.#search({ updateUrl: true })
      return
    }

    if (pageSizeOption) {
      event.preventDefault()
      this.#state.pageSize =
        Number(pageSizeOption.dataset.pageSizeValue) || this.#state.pageSize
      this.#state.page = 0
      this.#closePanels()
      this.#search({ updateUrl: true })
      return
    }

    if (activeFilter) {
      event.preventDefault()
      this.#removeActiveFilter(activeFilter)
      return
    }

    if (clearAll) {
      event.preventDefault()
      this.#state.facets = {}
      this.#state.ranges = {}
      this.#state.page = 0
      this.#search({ updateUrl: true })
      return
    }

    if (pagination) {
      event.preventDefault()
      this.#state.page = Number(pagination.dataset.page) || 0
      this.#search({ updateUrl: true, scroll: true })
      return
    }

    if (mobileFilters) {
      event.preventDefault()
      this.#openDrawer()
      return
    }

    if (drawerClose) {
      event.preventDefault()
      this.#closeDrawer()
      return
    }

    if (quickView) {
      event.preventDefault()
      this.#openQuickView(quickView)
      return
    }

    if (quickViewClose) {
      event.preventDefault()
      this.#closeQuickView()
      return
    }

    if (quickViewAdd) {
      event.preventDefault()
      this.#addQuickViewProduct(quickViewAdd)
      return
    }

    if (quickViewArrow) {
      event.preventDefault()
      this.#scrollQuickViewGallery()
      return
    }

    if (quickViewSize) {
      event.preventDefault()
      this.#selectQuickViewVariant(quickViewSize)
      return
    }

    if (quickViewSwatch) {
      event.preventDefault()
      this.#selectQuickViewSwatch(quickViewSwatch)
      return
    }

    if (swatch) {
      event.preventDefault()
      this.#selectSwatch(swatch)
      return
    }

    if (productLink) {
      this.#selectProduct(productLink)
    }
  }

  #handleDocumentPointerDown = event => {
    if (!this.#state.openPanel) {
      return
    }

    const toggle = this.#panelToggle(this.#state.openPanel)
    const panel = this.#panelForToggle(toggle)

    if (
      event.target.closest('[data-main-collection-drawer]') ||
      toggle?.contains(event.target) ||
      panel?.contains(event.target)
    ) {
      return
    }

    this.#closePanels()
  }

  #handleResize = () => {
    this.#positionOpenPanels()
  }

  #handleToggle = event => {
    if (event.target.matches('[data-main-collection-drawer]')) {
      if (event.newState === 'closed') {
        event.target.classList.remove('is-open', 'is-closing')
        document.body.style.overflow = ''
      }
      return
    }

    if (event.target.matches('[data-quick-view-overlay]')) {
      this.#quickView?.handleToggle(event)
    }
  }

  #handleInput = event => {
    const input = event.target.closest('[data-range-input], [data-range-text]')

    if (!input) {
      return
    }

    this.#syncRangeControls(input)
  }

  #handleChange = event => {
    const input = event.target.closest('[data-range-input], [data-range-text]')

    if (!input) {
      return
    }

    this.#syncRangeControls(input, { commit: true })
  }

  #handlePopState = () => {
    this.#state = {
      ...this.#state,
      ...this.#stateFromUrl(),
    }
    this.#syncRelatedLinksActiveState()
    this.#search({ updateUrl: false })
  }

  async #search(options = {}) {
    const { updateUrl = false, scroll = false } = options

    if (
      !this.#config.algolia.appId ||
      !this.#config.algolia.apiKey ||
      !this.#state.sortIndex
    ) {
      this.#renderMissingAlgolia()
      return
    }

    this.#setLoading(true)
    this.#searchAbortController?.abort()

    const searchAbortController = new AbortController()
    const searchSequence = this.#searchSequence + 1

    this.#searchAbortController = searchAbortController
    this.#searchSequence = searchSequence

    try {
      const requests = [
        {
          indexName: this.#state.sortIndex,
          params: this.#buildAlgoliaParams().toString(),
        },
        {
          indexName: this.#state.sortIndex,
          params: this.#buildFacetAlgoliaParams().toString(),
        },
      ]

      const shouldRequestSubcategoryFacet =
        this.#hasCategorySelection() &&
        this.#resolvedFilterOptions().some(
          filter => filter.value === CATEGORY_FILTER_ATTRIBUTE
        )

      if (shouldRequestSubcategoryFacet) {
        requests.push({
          indexName: this.#state.sortIndex,
          params: this.#buildFacetAlgoliaParams({
            attributes: [CATEGORY_FILTER_ATTRIBUTE],
            excludeFacetAttributes: [CATEGORY_FILTER_ATTRIBUTE],
            includeRefinements: true,
          }).toString(),
        })
      }

      const response = await fetch(
        ALGOLIA_QUERY_ENDPOINT(this.#config.algolia.appId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Algolia-API-Key': this.#config.algolia.apiKey,
            'X-Algolia-Application-Id': this.#config.algolia.appId,
          },
          body: JSON.stringify({ requests }),
          signal: searchAbortController.signal,
        }
      )

      if (!response.ok) {
        throw new Error('Algolia request failed')
      }

      const payload = await response.json()

      if (
        searchAbortController.signal.aborted ||
        searchSequence !== this.#searchSequence
      ) {
        return
      }

      this.#lastResponse = payload.results?.[0] || payload
      const facetResponse = payload.results?.[1] || this.#lastResponse

      if (shouldRequestSubcategoryFacet) {
        this.#lastFacetResponse = {
          ...facetResponse,
          facets: {
            ...(facetResponse.facets || {}),
            [CATEGORY_FILTER_ATTRIBUTE]:
              payload.results?.[2]?.facets?.[CATEGORY_FILTER_ATTRIBUTE] || {},
          },
        }
      } else {
        this.#lastFacetResponse = facetResponse
      }

      this.#render()

      if (updateUrl) {
        this.#writeUrl()
      }

      if (scroll) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#renderError()
      }
    } finally {
      if (searchSequence === this.#searchSequence) {
        this.#setLoading(false)
      }
    }
  }

  #buildAlgoliaParams() {
    const facets = this.#resolvedFilterOptions().map(filter => filter.value)
    const params = new URLSearchParams()

    params.set('query', '')
    params.set('hitsPerPage', String(this.#state.pageSize))
    params.set('page', String(this.#state.page))
    params.set('filters', this.#buildFilters())
    params.set('facets', JSON.stringify(facets))
    params.set('maxValuesPerFacet', '70')
    params.set('clickAnalytics', 'true')
    params.set('distinct', 'true')
    params.set('facetingAfterDistinct', 'true')
    params.set('ruleContexts', JSON.stringify([this.#config.collection.handle]))
    params.set('userToken', algoliaUserToken(this.#config))
    params.set(
      'attributesToRetrieve',
      JSON.stringify(algoliaProductAttributes(this.#config.market.handle))
    )
    params.set('attributesToHighlight', '[]')

    return params
  }

  #buildFacetAlgoliaParams(options = {}) {
    const {
      attributes = this.#resolvedFilterOptions().map(filter => filter.value),
      excludeFacetAttributes = [],
      includeRefinements = false,
    } = options
    const params = new URLSearchParams()

    params.set('query', '')
    params.set('hitsPerPage', '0')
    params.set('page', '0')
    params.set(
      'filters',
      this.#buildFilters({ excludeFacetAttributes, includeRefinements })
    )
    params.set('facets', JSON.stringify(attributes))
    params.set('maxValuesPerFacet', '70')
    params.set('clickAnalytics', 'false')
    params.set('distinct', 'true')
    params.set('facetingAfterDistinct', 'true')
    params.set('ruleContexts', JSON.stringify([this.#config.collection.handle]))
    params.set('userToken', algoliaUserToken(this.#config))

    return params
  }

  #buildFilters(options = {}) {
    const { excludeFacetAttributes = [], ...buildOptions } = options
    const excludedAttributes = new Set(excludeFacetAttributes)
    const facets = Object.fromEntries(
      Object.entries(this.#state.facets).filter(
        ([attribute]) => !excludedAttributes.has(attribute)
      )
    )

    return buildAlgoliaFilters({
      baseFilters: [
        `collections.handle:"${escapeAlgoliaFilterValue(this.#config.collection.handle)}"`,
        this.#config.algolia.defaultFilters,
      ],
      facets,
      ranges: this.#state.ranges,
      ...buildOptions,
    })
  }

  #render() {
    const response = this.#lastResponse
    const facetResponse = this.#lastFacetResponse || response
    const hits = Array.isArray(response?.hits) ? response.hits : []

    this.#renderTitle(response?.nbHits || 0)
    this.#renderSubtitle()
    this.#renderToolbar(facetResponse)
    this.#renderActiveFilters()
    this.#renderProducts(hits, response)
    this.#renderPagination(response)
    this.#renderDescription()

    if (this.#isDrawerOpen()) {
      this.#renderDrawer(facetResponse)
    }
  }

  #renderTitle(total) {
    const totalElement = this.querySelector('[data-main-collection-total]')

    if (!totalElement) {
      return
    }

    if (total > 0) {
      totalElement.hidden = false
      totalElement.textContent = `/ ${total} ${this.#config.strings.items}`
      return
    }

    totalElement.hidden = true
  }

  #renderSubtitle() {
    const subtitle = this.querySelector('[data-main-collection-subtitle]')
    const content = this.#dynamicDescription()

    subtitle.hidden = !content
    subtitle.innerHTML = content
  }

  #renderToolbar(response) {
    const toolbar = this.querySelector('[data-main-collection-toolbar]')

    if (!toolbar) {
      return
    }

    const facetsMarkup = this.#resolvedFilterOptions()
      .map(filter => this.#renderFacet(filter, response))
      .join('')

    toolbar.innerHTML = `
      <div class="algolia-filter">
        <div class="algolia-filter__inner">
          <div class="algolia-filter__facets-wrap">
            ${facetsMarkup}
          </div>

          <div class="algolia-filter__controls">
            ${this.#renderPageSizeControl()}
            ${this.#renderSortControl()}
          </div>
        </div>
      </div>
    `
    this.#positionOpenPanels()
  }

  #positionOpenPanels() {
    const toolbar = this.querySelector('[data-main-collection-toolbar]')

    if (!toolbar) {
      return
    }

    const panels = toolbar.querySelectorAll(
      '.main-collection__facet-panel, .main-collection__sort-panel, .main-collection__page-size-panel'
    )
    const viewportWidth = document.documentElement.clientWidth
    const gutter = 16

    panels.forEach(panel => {
      panel.classList.remove('is-flipped')

      if (panel.hidden) {
        return
      }

      const rect = panel.getBoundingClientRect()

      if (rect.right > viewportWidth - gutter) {
        panel.classList.add('is-flipped')
      }
    })
  }

  #renderDrawer(response) {
    const drawerContent = this.querySelector(
      '[data-main-collection-drawer-content]'
    )

    if (!drawerContent) {
      return
    }

    drawerContent.innerHTML = this.#resolvedFilterOptions()
      .map(filter => this.#renderFacet(filter, response, { mobile: true }))
      .join('')

    this.#syncMobileAccordions()
  }

  #renderFacet(filter, response, options = {}) {
    const { mobile = false } = options
    const attribute = filter.value
    const panelId = `${mobile ? 'mobile-' : ''}facet-${this.#handleize(attribute)}`
    const isOpen = this.#state.openPanel === panelId
    const panelState = this.#panelState(panelId, { isOpen, mobile })

    if (isPriceAttribute(attribute)) {
      return this.#renderRangeFacet(filter, response, panelId, panelState)
    }

    const facetValues = Object.entries(response?.facets?.[attribute] || {})
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => {
        if (attribute === 'sizesAvailable') {
          return this.#sizeSort(a.value, b.value)
        }

        return b.count - a.count
      })

    if (!facetValues.length) {
      return ''
    }

    const isColour = this.#isColourAttribute(attribute)
    const hasLongLabels = facetValues.some(
      facet => this.#formatFacetLabel(attribute, facet.value).length > 12
    )
    const panelClasses = [
      'main-collection__facet-panel',
      'main-collection__facet-panel--options',
      isColour ? 'main-collection__facet-panel--colour' : '',
      hasLongLabels || attribute === 'sizesAvailable'
        ? 'main-collection__facet-panel--wide'
        : '',
      panelState.className,
    ]
      .filter(Boolean)
      .join(' ')
    const selectedCount = this.#facetSelectedCount(attribute)
    const wrapperClasses = [
      'main-collection__facet',
      mobile && panelState.isOpen ? 'is-active' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return `
      <div class="${this.#escapeHtml(wrapperClasses)}" data-facet="${this.#escapeHtml(attribute)}">
        <button
          class="main-collection__facet-button text-eyebrow-2xs"
          type="button"
          data-panel-toggle="${this.#escapeHtml(panelId)}"
          aria-expanded="${isOpen ? 'true' : 'false'}"
        >
          <span>${this.#escapeHtml(filter.label)}</span>
          ${this.#icon('chevron-down')}
        </button>

        <div class="${this.#escapeHtml(panelClasses)}" ${panelState.isVisible ? '' : 'hidden'}>
          <ul class="main-collection__facet-list ${isColour ? 'main-collection__facet-list--colour' : ''}" role="list">
            ${facetValues.map(facet => this.#renderFacetValue(attribute, facet)).join('')}
          </ul>

          ${this.#renderFacetFooter(attribute, selectedCount)}
        </div>
      </div>
    `
  }

  #renderFacetValue(attribute, facet) {
    const selected =
      this.#state.facets[attribute]?.includes(facet.value) || false
    const isColour = this.#isColourAttribute(attribute)
    const label = this.#formatFacetLabel(attribute, facet.value)

    return `
      <li>
        <button
          class="main-collection__facet-option ${isColour ? 'main-collection__facet-option--colour' : ''} text-body-sm ${selected ? 'is-refined' : ''}"
          type="button"
          data-facet-attribute="${this.#escapeHtml(attribute)}"
          data-facet-value="${this.#escapeHtml(facet.value)}"
          aria-label="${this.#escapeHtml(`${label} (${String(facet.count).padStart(2, '0')})`)}"
        >
          ${isColour ? this.#renderFacetSwatch(facet.value) : '<span class="main-collection__facet-checkbox"></span>'}
          <span class="${isColour ? 'main-collection__visually-hidden' : ''}">${this.#escapeHtml(label)}</span>
          <span class="main-collection__facet-count text-body-sm">(${String(facet.count).padStart(2, '0')})</span>
        </button>
      </li>
    `
  }

  #renderFacetFooter(attribute, selectedCount) {
    return `
      <div class="main-collection__facet-footer">
        <span class="main-collection__facet-selected text-eyebrow-2xs">
          ${this.#escapeHtml(this.#config.strings.selected.replace('__count__', selectedCount))}
        </span>

        <button class="main-collection__facet-reset text-eyebrow-2xs" type="button" data-active-filter data-filter-attribute="${this.#escapeHtml(attribute)}">
          ${this.#escapeHtml(this.#config.strings.reset)}
        </button>
      </div>
    `
  }

  #facetSelectedCount(attribute) {
    return this.#state.facets[attribute]?.length || 0
  }

  #renderRangeFacet(filter, response, panelId, panelState) {
    const attribute = filter.value
    const stats = response?.facets_stats?.[attribute]

    if (!stats || stats.min === stats.max) {
      return ''
    }

    const selected = this.#state.ranges[attribute] || {}
    const bounds = rangeBounds(stats)
    const min = selected.min ?? Math.floor(stats.min)
    const max = selected.max ?? Math.ceil(stats.max)
    const ticks = rangeTicks(bounds.min, bounds.max)
    const wrapperClasses = [
      'main-collection__facet',
      panelId.startsWith('mobile-') && panelState.isOpen ? 'is-active' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return `
      <div class="${this.#escapeHtml(wrapperClasses)}" data-facet="${this.#escapeHtml(attribute)}">
        <button
          class="main-collection__facet-button text-eyebrow-2xs"
          type="button"
          data-panel-toggle="${this.#escapeHtml(panelId)}"
          aria-expanded="${panelState.isOpen ? 'true' : 'false'}"
        >
          <span>${this.#escapeHtml(filter.label)}</span>
          ${this.#icon('chevron-down')}
        </button>

        <div class="main-collection__facet-panel main-collection__facet-panel--range ${this.#escapeHtml(panelState.className)}" ${panelState.isVisible ? '' : 'hidden'}>
          <div
            class="algolia-range-slider"
            data-range-slider
            data-range-attribute="${this.#escapeHtml(attribute)}"
            data-range-min="${this.#escapeHtml(bounds.min)}"
            data-range-max="${this.#escapeHtml(bounds.max)}"
            style="${rangeStyle(min, max, bounds)}"
          >
            <div class="algolia-range-slider__inputs">
              ${this.#rangeTextInput(attribute, 'min', min, bounds.min, max)}
              <div class="algolia-range-slider__inputs-divider" aria-hidden="true"></div>
              ${this.#rangeTextInput(attribute, 'max', max, min, bounds.max)}
            </div>

            <div class="algolia-range-slider__wrap" role="group" aria-label="${this.#escapeHtml(filter.label)}">
              <label class="main-collection__visually-hidden" for="${this.#escapeHtml(panelId)}-min">${this.#escapeHtml(this.#config.strings.priceMin)}</label>

              <input
                id="${this.#escapeHtml(panelId)}-min"
                aria-label="${this.#escapeHtml(this.#config.strings.priceMin)}"
                data-range-input
                data-range-attribute="${this.#escapeHtml(attribute)}"
                data-range-side="min"
                type="range"
                min="${this.#escapeHtml(bounds.min)}"
                max="${this.#escapeHtml(bounds.max)}"
                step="1"
                value="${this.#escapeHtml(min)}"
              >

              <label class="main-collection__visually-hidden" for="${this.#escapeHtml(panelId)}-max">${this.#escapeHtml(this.#config.strings.priceMax)}</label>

              <input
                id="${this.#escapeHtml(panelId)}-max"
                aria-label="${this.#escapeHtml(this.#config.strings.priceMax)}"
                data-range-input
                data-range-attribute="${this.#escapeHtml(attribute)}"
                data-range-side="max"
                type="range"
                min="${this.#escapeHtml(bounds.min)}"
                max="${this.#escapeHtml(bounds.max)}"
                step="1"
                value="${this.#escapeHtml(max)}"
              >
            </div>

            <div class="algolia-range-slider__indicators">
              ${ticks
                .map(
                  tick => `
                    <div class="algolia-range-slider__indicators-notch">
                      <div class="algolia-range-slider__indicators-line"></div>
                      <p class="algolia-range-slider__indicators-value text-body-sm">${this.#escapeHtml(tick)}</p>
                    </div>
                  `
                )
                .join('')}
            </div>
          </div>

          <div class="main-collection__range-footer">
            <button class="main-collection__range-reset text-eyebrow-2xs" type="button" data-range-reset="${this.#escapeHtml(attribute)}">
              ${this.#escapeHtml(this.#config.strings.reset)}
            </button>
          </div>
        </div>
      </div>
    `
  }

  #rangeTextInput(attribute, side, value, min, max) {
    const label =
      side === 'min'
        ? this.#config.strings.priceMin
        : this.#config.strings.priceMax

    return `
      <div class="algolia-range-slider__inputs-wrapper">
        <span class="algolia-range-slider__inputs-currency text-eyebrow-2xs">${this.#escapeHtml(this.#config.market.currencySymbol || '')}</span>
        <input
          aria-label="${label}"
          class="algolia-range-slider__inputs-input text-body-sm"
          data-range-text
          data-range-attribute="${this.#escapeHtml(attribute)}"
          data-range-side="${side}"
          inputmode="numeric"
          min="${this.#escapeHtml(min)}"
          max="${this.#escapeHtml(max)}"
          type="text"
          value="${this.#escapeHtml(value)}"
        >
      </div>
    `
  }

  #renderPageSizeControl() {
    const panelId = 'page-size'
    const isOpen = this.#state.openPanel === panelId
    const panelState = this.#panelState(panelId, { isOpen })

    return `
      <div class="main-collection__facet main-collection__page-size">
        <button class="main-collection__page-size-button text-eyebrow-2xs" type="button" data-panel-toggle="${panelId}" aria-expanded="${isOpen ? 'true' : 'false'}">
          <span>${this.#escapeHtml(this.#config.strings.pageSize.replace('__size__', this.#state.pageSize))}</span>
          ${this.#icon('chevron-down')}
        </button>

        <div class="main-collection__page-size-panel ${this.#escapeHtml(panelState.className)}" ${panelState.isVisible ? '' : 'hidden'}>
          ${this.#config.pagination.pageSizes
            .map(
              pageSize => `
            <button class="main-collection__facet-option text-body-sm ${Number(pageSize) === this.#state.pageSize ? 'is-refined' : ''}" type="button" data-page-size-value="${this.#escapeHtml(pageSize)}">
              ${this.#escapeHtml(pageSize)}
            </button>
          `
            )
            .join('')}
        </div>
      </div>
    `
  }

  #renderSortControl() {
    const panelId = 'sort'
    const isOpen = this.#state.openPanel === panelId
    const panelState = this.#panelState(panelId, { isOpen })
    const active =
      this.#config.algolia.sortByOptions.find(
        sort => sort.value === this.#state.sortIndex
      ) || this.#config.algolia.sortByOptions[0]

    return `
      <div class="main-collection__facet">
        <button class="main-collection__sort-button text-eyebrow-2xs" type="button" data-panel-toggle="${panelId}" aria-expanded="${isOpen ? 'true' : 'false'}">
          <span>${this.#escapeHtml(active?.label || this.#config.strings.sortBy)}</span>
          ${this.#icon('chevron-down')}
        </button>

        <div class="main-collection__sort-panel ${this.#escapeHtml(panelState.className)}" role="radiogroup" ${panelState.isVisible ? '' : 'hidden'}>
          ${this.#config.algolia.sortByOptions
            .map(
              sort => `
            <button class="main-collection__sort-option text-body-sm ${sort.value === this.#state.sortIndex ? 'is-refined' : ''}" type="button" data-sort-value="${this.#escapeHtml(sort.value)}" role="radio" aria-checked="${sort.value === this.#state.sortIndex ? 'true' : 'false'}">
              <span class="main-collection__facet-radio"></span>
              <span>${this.#escapeHtml(sort.label)}</span>
            </button>
          `
            )
            .join('')}
        </div>
      </div>
    `
  }

  #renderActiveFilters() {
    const container = this.querySelector(
      '[data-main-collection-active-filters]'
    )
    const drawerContainer = this.querySelector(
      '[data-main-collection-drawer-active-filters]'
    )
    const drawerClear = this.querySelector('.main-collection__drawer-clear')
    const activeFilters = this.#activeFilters()

    if (!container && !drawerContainer) {
      return
    }

    if (!activeFilters.length) {
      if (container) {
        container.hidden = true
        container.innerHTML = ''
      }
      if (drawerContainer) {
        drawerContainer.hidden = true
        drawerContainer.innerHTML = ''
      }
      if (drawerClear) {
        drawerClear.hidden = true
      }
      return
    }

    if (container) {
      container.hidden = false
      container.innerHTML = `
        ${this.#activeFilterButtons(activeFilters, 'main-collection__active-filter text-body-sm')}

        <button class="main-collection__active-clear text-body-sm" type="button" data-clear-all>
          ${this.#escapeHtml(this.#config.strings.clearAll)}
        </button>
      `
    }

    if (drawerContainer) {
      drawerContainer.hidden = false
      drawerContainer.innerHTML = this.#activeFilterButtons(
        activeFilters,
        'main-collection__drawer-active-filter text-eyebrow-2xs'
      )
    }

    if (drawerClear) {
      drawerClear.hidden = false
    }
  }

  #activeFilters() {
    const activeFilters = []

    Object.entries(this.#state.facets).forEach(([attribute, values]) => {
      values.forEach(value => {
        activeFilters.push({
          attribute,
          value,
          label: this.#formatFacetLabel(attribute, value),
        })
      })
    })

    Object.entries(this.#state.ranges).forEach(([attribute, range]) => {
      if (range.min !== null && range.min !== undefined && range.min !== '') {
        activeFilters.push({
          attribute,
          label: `≥ ${this.#formatFacetLabel(attribute, range.min)}`,
          rangeSide: 'min',
        })
      }

      if (range.max !== null && range.max !== undefined && range.max !== '') {
        activeFilters.push({
          attribute,
          label: `≤ ${this.#formatFacetLabel(attribute, range.max)}`,
          rangeSide: 'max',
        })
      }
    })

    return activeFilters
  }

  #activeFilterButtons(activeFilters, className) {
    return activeFilters
      .map(
        filter => `
        <button
          class="${this.#escapeHtml(className)}"
          type="button"
          data-active-filter
          data-filter-attribute="${this.#escapeHtml(filter.attribute)}"
          ${filter.value ? `data-filter-value="${this.#escapeHtml(filter.value)}"` : ''}
          ${filter.rangeSide ? `data-filter-range-side="${this.#escapeHtml(filter.rangeSide)}"` : ''}
        >
          <span>${this.#escapeHtml(filter.label)}</span>
          ${this.#icon('close')}
        </button>
      `
      )
      .join('')
  }

  #renderProducts(hits, response) {
    const grid = this.querySelector('[data-main-collection-grid]')
    const count = this.querySelector('[data-main-collection-product-count]')

    if (!grid) {
      return
    }

    if (!hits.length) {
      grid.innerHTML = `<p class="main-collection__empty text-body-md">${this.#escapeHtml(this.#config.strings.noProducts)}</p>`
      if (count) {
        count.hidden = true
      }
      return
    }

    const banners = this.#mapContentBanners(response?.userData || [])
    const cards = []

    hits.forEach((hit, index) => {
      const position =
        (response.page || 0) * (response.hitsPerPage || this.#state.pageSize) +
        index +
        1
      const product = this.#formatProduct(hit, {
        indexName: this.#state.sortIndex,
        position,
        queryID: response.queryID,
      })
      cards.push(this.#cardRenderer.render(product, index))

      const banner = banners.get(position)
      if (banner) {
        cards.push(this.#renderContentBanner(banner))
      }
    })

    grid.innerHTML = cards.join('')
    this.#hydrateCards(grid)

    if (count) {
      count.hidden = false
      count.textContent = this.#config.strings.productCount
        .replace('__products__', String(hits.length))
        .replace('__total__', String(response.nbHits || hits.length))
    }
  }

  #renderContentBanner(banner) {
    const layout =
      banner[`${this.#columnCount()}-grid-layout`] ||
      banner['4-grid-layout'] ||
      {}
    const columnSpan = Math.min(
      Number(layout.column_span || 1),
      this.#columnCount() === 2 ? 2 : this.#columnCount() - 1
    )
    const rowSpan = Number(layout.row_span || 1)
    const imageSource = value =>
      typeof value === 'string' ? value : value?.src || ''
    const desktopImage =
      imageSource(banner.imageDesktop) || imageSource(banner.image)
    const mobileImage = imageSource(banner.imageMobile) || desktopImage
    const imageSrcset = source =>
      [400, 800, 1200]
        .map(width => `${imageUrl({ src: source }, width)} ${width}w`)
        .join(',')
    const mobileImageSizes = columnSpan > 1 ? '100vw' : '50vw'
    const desktopImageSizes = `${(columnSpan / this.#columnCount()) * 100}vw`
    const imageAlt =
      banner.imageDesktop?.alt ||
      banner.imageMobile?.alt ||
      banner.image?.alt ||
      banner.bannerTitle ||
      ''
    const isLarge = columnSpan > 1 || rowSpan > 1 || banner.textSize === 'large'
    const eyebrow = banner.bannerEyebrow || banner.eyebrow || ''
    const body = banner.bannerBody || banner.body || banner.description || ''
    const countdown =
      banner.countdownText || banner.countdownLabel || banner.countdown || ''
    const titleClass = isLarge ? 'text-title-serif-lg' : 'text-body-md'
    const bannerClasses = [
      'content-banner',
      isLarge ? 'content-banner--large' : 'content-banner--simple',
      rowSpan > 1 ? 'content-banner--tall' : '',
    ]
      .filter(Boolean)
      .join(' ')
    const horizontalAlignment =
      banner.horizontalAlignment || (isLarge ? 'center' : 'left')
    const verticalAlignment =
      banner.verticalAlignment || (isLarge ? 'center' : 'bottom')
    const defaultOverlayOpacity = isLarge ? 0.4 : 0.2
    const configuredOverlayOpacity = Number(banner.overlayOpacity)
    const overlayOpacity =
      banner.overlayOpacity !== undefined &&
      banner.overlayOpacity !== null &&
      Number.isFinite(configuredOverlayOpacity)
        ? clamp(configuredOverlayOpacity / 100, 0, 1)
        : defaultOverlayOpacity
    const styles = [
      `--column-span: ${columnSpan}`,
      `--row-span: ${rowSpan}`,
      banner.textColor ? `--text-color: ${banner.textColor}` : '',
      `--overlay-opacity: ${overlayOpacity}`,
      `--horizontal-alignment: ${this.#alignmentValue(horizontalAlignment)}`,
      `--text-align: ${horizontalAlignment}`,
      `--vertical-alignment: ${this.#alignmentValue(verticalAlignment)}`,
    ]
      .filter(Boolean)
      .join(';')
    const imageHref = this.#safeHref(banner.imageLink || banner.bannerLink)
    const buttonHref = this.#safeHref(banner.bannerLink)
    const imageLinkLabel =
      imageAlt ||
      banner.bannerLinkText ||
      this.#config.strings.quickViewViewProduct
    const imageMarkup = `
      <picture>
        ${mobileImage ? `<source media="(width < 1024px)" srcset="${this.#escapeHtml(imageSrcset(mobileImage))}" sizes="${mobileImageSizes}">` : ''}
        <img src="${this.#escapeHtml(imageUrl({ src: desktopImage || mobileImage }, 1200))}" srcset="${this.#escapeHtml(imageSrcset(desktopImage || mobileImage))}" sizes="${desktopImageSizes}" alt="${this.#escapeHtml(imageAlt)}" loading="lazy">
      </picture>
    `
    const buttonMarkup =
      buttonHref && banner.bannerLinkText
        ? `
          <a class="button button--underlined ${isLarge ? '' : 'button--no-icon'} content-banner__button" href="${this.#escapeHtml(buttonHref)}">
            <span class="button__label">${this.#escapeHtml(banner.bannerLinkText)}</span>
            ${isLarge ? `<span class="button__icon">${this.#icon('arrow-right')}</span>` : ''}
          </a>
        `
        : ''

    return `
      <div class="${this.#escapeHtml(bannerClasses)}" style="${this.#escapeHtml(styles)}">
        ${
          desktopImage || mobileImage
            ? `
          ${
            imageHref
              ? `<a class="content-banner__image" href="${this.#escapeHtml(imageHref)}" aria-label="${this.#escapeHtml(imageLinkLabel)}">${imageMarkup}</a>`
              : `<div class="content-banner__image">${imageMarkup}</div>`
          }
        `
            : ''
        }

        <div class="content-banner__content">
          <div class="content-banner__copy">
            ${isLarge && eyebrow ? `<p class="content-banner__eyebrow text-eyebrow-2xs">${this.#escapeHtml(eyebrow)}</p>` : ''}
            ${banner.bannerTitle ? `<p class="content-banner__title ${titleClass}">${this.#escapeHtml(banner.bannerTitle)}</p>` : ''}
            ${isLarge && body ? `<p class="content-banner__body text-body-xs">${this.#escapeHtml(body)}</p>` : ''}
            ${isLarge && countdown ? `<p class="content-banner__countdown text-title-sans-md">${this.#escapeHtml(countdown)}</p>` : ''}
          </div>

          ${buttonMarkup}
        </div>
      </div>
    `
  }

  #safeHref(value) {
    const href = String(value || '').trim()

    if (!href || /[\u0000-\u001F\u007F]/.test(href)) {
      return ''
    }

    try {
      const url = new URL(href, window.location.origin)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return ''
      }

      return getLocalisedRoute(href)
    } catch {
      return ''
    }
  }

  #renderPagination(response) {
    const pagination = this.querySelector('[data-main-collection-pagination]')
    const totalPages = response?.nbPages || 0

    if (!pagination || totalPages <= 1) {
      if (pagination) {
        pagination.hidden = true
        pagination.innerHTML = ''
      }
      return
    }

    pagination.hidden = false
    pagination.innerHTML = `
      <button class="main-collection__pagination-button" type="button" aria-label="${this.#escapeHtml(this.#config.strings.previousPage)}" data-page="${Math.max(this.#state.page - 1, 0)}" ${this.#state.page === 0 ? 'disabled' : ''}>${this.#icon('chevron-left')}</button>
      ${this.#visiblePages(this.#state.page, totalPages)
        .map(page => {
          if (page === null) {
            return '<span class="main-collection__pagination-ellipsis text-body-sm">...</span>'
          }

          return `<button class="main-collection__pagination-button text-body-sm ${page === this.#state.page ? 'is-active' : ''}" type="button" data-page="${page}">${page + 1}</button>`
        })
        .join('')}
      <button class="main-collection__pagination-button" type="button" aria-label="${this.#escapeHtml(this.#config.strings.nextPage)}" data-page="${Math.min(this.#state.page + 1, totalPages - 1)}" ${this.#state.page >= totalPages - 1 ? 'disabled' : ''}>${this.#icon('chevron-right')}</button>
    `
  }

  #renderDescription() {
    const description = this.querySelector('[data-main-collection-description]')

    if (!description) {
      return
    }

    const content = this.#config.collection.description || ''

    description.hidden = !content
    description.innerHTML = content
  }

  #renderMissingAlgolia() {
    const grid = this.querySelector('[data-main-collection-grid]')

    if (!grid) {
      return
    }

    this.#setLoading(false)
  }

  #renderError() {
    const grid = this.querySelector('[data-main-collection-grid]')

    if (grid) {
      grid.innerHTML = `<p class="main-collection__empty text-body-md">${this.#escapeHtml(this.#config.strings.noProducts)}</p>`
    }
  }

  #hydrateCards(scope) {
    const cards = scope.matches?.('[data-product-card]')
      ? [scope]
      : [...scope.querySelectorAll('[data-product-card]')]
    const productsByCard = new Map()
    const familyTags = []

    cards.forEach(card => {
      const product = this.#cardProduct(card)

      if (!product) {
        return
      }

      const familyTag = this.#fetcher.familyTag(product)

      productsByCard.set(card, product)

      if (familyTag) {
        familyTags.push(familyTag)
      }
    })

    this.#prefetchCardSwatches(familyTags)

    productsByCard.forEach((product, card) => {
      this.#observeCardSwatches(card, product)
    })
  }

  #prefetchCardSwatches(familyTags) {
    if (!familyTags.length) {
      return
    }

    void this.#fetcher.fetchRelatedProductsBatch(familyTags)
  }

  #observeCardSwatches(card, product) {
    if (this.#hydratedSwatches.has(card) || !this.#fetcher.familyTag(product)) {
      return
    }

    if (!('IntersectionObserver' in window)) {
      this.#hydrateCardSwatches(card)
      return
    }

    if (!this.#swatchObserver) {
      this.#swatchObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) {
              return
            }

            this.#swatchObserver?.unobserve(entry.target)
            this.#hydrateCardSwatches(entry.target)
          })
        },
        { rootMargin: '600px 0px' }
      )
    }

    this.#swatchObserver.observe(card)
  }

  async #hydrateCardSwatches(card) {
    if (this.#hydratedSwatches.has(card)) {
      return
    }

    this.#hydratedSwatches.add(card)

    const product = this.#cardProduct(card)

    if (!product) {
      return
    }

    const familyTag = this.#fetcher.familyTag(product)

    if (!familyTag) {
      return
    }

    const relatedProducts =
      (await this.#fetcher.fetchRelatedProductsBatch([familyTag])).get(
        familyTag
      ) || []

    if (!card.isConnected) {
      return
    }

    this.#cardRenderer.renderSwatches(
      card,
      product,
      this.#fetcher.filterColourProducts(product, relatedProducts)
    )
  }

  #selectSwatch(swatch) {
    const card = swatch.closest('[data-product-card]')
    const product = this.#parseJson(swatch.dataset.product)

    if (!card || !product) {
      return
    }

    const currentProduct = this.#cardProduct(card)
    const selectedProduct = {
      ...product,
      __indexName: currentProduct?.__indexName || product.__indexName,
      __position: currentProduct?.__position || product.__position,
      __queryID: currentProduct?.__queryID || product.__queryID,
    }
    const index = Number(card.dataset.productIndex) || 0
    const rendered = this.#cardRenderer.render(selectedProduct, index)
    const template = document.createElement('template')
    template.innerHTML = rendered.trim()
    const newCard = template.content.firstElementChild

    if (newCard) {
      card.replaceWith(newCard)
      this.#hydrateCards(newCard)
      this.#hydrateCardSwatches(newCard)
    }
  }

  async #openQuickView(button) {
    if (!this.#quickViewPromise) {
      this.#quickViewPromise = import('@lyle/algolia-product-card-quick-view')
    }

    const { AlgoliaProductCardQuickView } = await this.#quickViewPromise
    if (!this.isConnected) return

    if (!this.#quickView) {
      this.#quickView = new AlgoliaProductCardQuickView({
        callbacks: {
          cardProduct: card => this.#cardProduct(card),
          familyTag: product => this.#fetcher.familyTag(product),
          fetchProductJson: handle => this.#fetcher.fetchProductJson(handle),
          fetchRelatedProductsBatch: tags =>
            this.#fetcher.fetchRelatedProductsBatch(tags),
          filterColourProducts: (product, relatedProducts) =>
            this.#fetcher.filterColourProducts(product, relatedProducts),
          formatMoney: value => this.#formatMoney(value),
          sendConversionEvent: (quickViewButton, context) =>
            this.#sendConversionEvent(quickViewButton, context),
        },
        config: this.#config,
        icons: this.#iconMarkup,
        root: this,
      })
    }

    await this.#quickView.open(button)
  }

  #closeQuickView() {
    this.#quickView?.close()
  }

  #selectQuickViewVariant(button) {
    this.#quickView?.selectVariant(button)
  }

  #selectQuickViewSwatch(button) {
    void this.#quickView?.selectSwatch(button)
  }

  #addQuickViewProduct(button) {
    void this.#quickView?.add(button)
  }

  #scrollQuickViewGallery() {
    this.#quickView?.scrollGallery()
  }

  #selectProduct(link) {
    this.#rememberSelectedProduct(link)
    this.#sendClickEvent(link)
  }

  #rememberSelectedProduct(element) {
    const productId = element.closest('[data-product-card]')?.dataset.productId

    try {
      if (productId) {
        sessionStorage.setItem(SELECTED_PRODUCT_KEY, productId)
      }

      if (element.dataset.queryId) {
        sessionStorage.setItem(SELECTED_QUERY_KEY, element.dataset.queryId)
      } else {
        sessionStorage.removeItem(SELECTED_QUERY_KEY)
      }
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  #formatProduct(hit, options = {}) {
    return this.#fetcher.formatProduct(hit, options)
  }

  #dynamicDescription() {
    const descriptions = this.#config.collection.descriptions || []
    const threshold = this.#config.market.giftThreshold || 0

    if (!descriptions.length) {
      return this.#replaceDescriptionPlaceholders(
        this.#config.collection.bannerSubtitle || '',
        threshold
      )
    }

    const activeValues = Object.values(this.#state.facets).flat()
    const filterMatch = descriptions
      .filter(description => {
        const required = Array.isArray(description.requiredFilters)
          ? description.requiredFilters
          : []
        return (
          required.length &&
          required.every(filter => activeValues.includes(filter))
        )
      })
      .sort(
        (a, b) =>
          (b.requiredFilters?.length || 0) - (a.requiredFilters?.length || 0)
      )[0]

    return this.#replaceDescriptionPlaceholders(
      filterMatch?.description || this.#config.collection.bannerSubtitle || '',
      threshold
    )
  }

  #replaceDescriptionPlaceholders(text, threshold = 0) {
    if (!text) {
      return ''
    }

    return String(text)
      .replace(
        /\{currency_symbol\}/gi,
        this.#config.market.currencySymbol || ''
      )
      .replace(/\{currency_code\}/gi, this.#config.market.currencyCode || '')
      .replace(/\{market_handle\}/gi, this.#config.market.handle || '')
      .replace(/\{(?:fgwp_)?threshold\}/gi, () => threshold)
  }

  #stateFromUrl(search = window.location.search) {
    const params = new URLSearchParams(search)
    const state = {
      facets: {},
      page: 0,
      ranges: {},
    }

    if (params.get('page')) {
      state.page = this.#pageIndexFromParam(params.get('page'))
    }

    if (params.get('page_size')) {
      state.pageSize = Number(params.get('page_size')) || this.#state.pageSize
    }

    if (params.get('sort')) {
      const sort = this.#config.algolia.sortByOptions.find(
        option => option.id === params.get('sort')
      )
      state.sortIndex = sort?.value || this.#state.sortIndex
    }

    this.#resolvedFilterOptions(true).forEach(filter => {
      const key = this.#urlKey(filter)
      const value = params.get(key)

      if (!value) {
        return
      }

      if (isPriceAttribute(filter.value) && value.includes(':')) {
        const [min, max] = value.split(':')
        state.ranges[filter.value] = { min, max }
        return
      }

      state.facets[filter.value] = value
        .split(',')
        .map(item => this.#unCleanValue(item, filter.value))
        .filter(Boolean)
    })

    return state
  }

  #pageIndexFromParam(value) {
    const page = Number(value)

    if (!Number.isInteger(page) || page < 1) {
      return 0
    }

    return page - 1
  }

  #writeUrl() {
    const params = new URLSearchParams()

    if (this.#state.page > 0) {
      params.set('page', String(this.#state.page + 1))
    }

    if (
      this.#state.pageSize !== Number(this.#config.pagination.defaultPageSize)
    ) {
      params.set('page_size', String(this.#state.pageSize))
    }

    const sort = this.#config.algolia.sortByOptions.find(
      option => option.value === this.#state.sortIndex
    )

    if (sort && sort.id !== 'relevance') {
      params.set('sort', sort.id)
    }

    this.#resolvedFilterOptions().forEach(filter => {
      const key = this.#urlKey(filter)

      if (this.#state.facets[filter.value]?.length) {
        params.set(
          key,
          this.#state.facets[filter.value]
            .map(value => this.#cleanValue(value, filter.value))
            .join(',')
        )
      }

      if (this.#state.ranges[filter.value]) {
        const range = this.#state.ranges[filter.value]
        params.set(key, `${range.min || ''}:${range.max || ''}`)
      }
    })

    const query = params.toString()
    const url = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname
    window.history.pushState({}, '', url)
    this.#syncRelatedLinksActiveState()
  }

  #applyRelatedLink(link) {
    let url

    try {
      url = new URL(link.href, window.location.href)
    } catch {
      return false
    }

    if (url.pathname !== window.location.pathname) {
      return false
    }

    this.#state = {
      ...this.#state,
      ...this.#stateFromUrl(url.search),
    }
    this.#search({ updateUrl: true })

    return true
  }

  #syncRelatedLinksActiveState() {
    const current = new URL(window.location.href)
    const ignoreKeys = new Set(['page', 'page_size', 'sort'])

    this.querySelectorAll('.main-collection__quick-links').forEach(group => {
      const links = [...group.querySelectorAll('a.related-links__button')]
      let bestScore = -1
      const bestLinks = []

      for (const link of links) {
        const score = this.#relatedLinkMatchScore(
          link.getAttribute('href'),
          current,
          ignoreKeys
        )

        if (score < 0) {
          continue
        }

        if (score > bestScore) {
          bestScore = score
          bestLinks.length = 0
          bestLinks.push(link)
          continue
        }

        if (score === bestScore) {
          bestLinks.push(link)
        }
      }

      for (const link of links) {
        const active = bestLinks.includes(link)

        link.classList.toggle('is-active', active)

        if (active) {
          link.setAttribute('aria-current', 'page')
        } else {
          link.removeAttribute('aria-current')
        }
      }
    })
  }

  #relatedLinkMatchScore(href, current, ignoreKeys) {
    let linkUrl

    try {
      linkUrl = new URL(href, current.href)
    } catch {
      return -1
    }

    if (linkUrl.pathname !== current.pathname) {
      return -1
    }

    const linkParams = [...linkUrl.searchParams.entries()]
      .map(([key, value]) => [key.toLowerCase(), value])
      .filter(([key]) => !ignoreKeys.has(key))
    const currentValues = this.#paramValuesByKey(
      current.searchParams,
      ignoreKeys
    )

    if (linkParams.length === 0) {
      return [...currentValues.keys()].length === 0 ? 0 : -1
    }

    for (const [key, value] of linkParams) {
      const values = currentValues.get(key)

      if (!values) {
        return -1
      }

      const linkValues = value
        .split(',')
        .map(item => this.#normalizeRelatedLinkParamValue(item))
        .filter(Boolean)

      if (!linkValues.every(item => values.has(item))) {
        return -1
      }
    }

    return linkParams.length
  }

  #paramValuesByKey(searchParams, ignoreKeys) {
    const valuesByKey = new Map()

    for (const [rawKey, value] of searchParams.entries()) {
      const key = rawKey.toLowerCase()

      if (ignoreKeys.has(key)) {
        continue
      }

      if (!valuesByKey.has(key)) {
        valuesByKey.set(key, new Set())
      }

      for (const item of value.split(',')) {
        const normalized = this.#normalizeRelatedLinkParamValue(item)

        if (normalized) {
          valuesByKey.get(key).add(normalized)
        }
      }
    }

    return valuesByKey
  }

  #normalizeRelatedLinkParamValue(value) {
    try {
      return decodeURIComponent(String(value)).trim().replace(/ /g, '-')
    } catch {
      return String(value).trim().replace(/ /g, '-')
    }
  }

  #panelState(panelId, options = {}) {
    const { isOpen = false, mobile = false } = options
    const isClosing = !mobile && this.#closingPanel === panelId
    const isOpening = !mobile && this.#openingPanel === panelId

    return {
      className: mobile
        ? isOpen
          ? 'is-open'
          : ''
        : isClosing
          ? 'is-closing'
          : isOpening
            ? 'is-opening'
            : isOpen
              ? 'is-open'
              : '',
      isClosing,
      isOpen,
      isVisible: mobile || isOpen || isClosing,
    }
  }

  #togglePanel(panelId) {
    if (panelId.startsWith('mobile-')) {
      this.#state.openPanel = this.#state.openPanel === panelId ? null : panelId
      this.#syncMobileAccordions()
      return
    }

    const previousPanel = this.#state.openPanel

    window.clearTimeout(this.#panelAnimationTimer)
    window.clearTimeout(this.#panelOpenAnimationTimer)

    if (previousPanel === panelId) {
      this.#closingPanel = panelId
      this.#openingPanel = null
      this.#state.openPanel = null
      this.#syncDesktopPanels([panelId])
      this.#schedulePanelCloseCleanup(panelId)
      return
    }

    this.#closingPanel = previousPanel
    this.#openingPanel = panelId
    this.#state.openPanel = panelId
    this.#syncDesktopPanels([previousPanel, panelId])
    this.#schedulePanelOpenCleanup(panelId)

    if (previousPanel) {
      this.#schedulePanelCloseCleanup(previousPanel)
    }
  }

  #closePanels() {
    const panelId = this.#state.openPanel

    if (this.#state.openPanel) {
      this.#closingPanel = this.#state.openPanel
      this.#schedulePanelCloseCleanup(this.#closingPanel)
    }

    this.#openingPanel = null
    this.#state.openPanel = null

    if (panelId?.startsWith('mobile-')) {
      this.#syncMobileAccordions()
      return
    }

    this.#syncDesktopPanels([panelId])
  }

  #syncMobileAccordions() {
    const drawer = this.querySelector('[data-main-collection-drawer]')

    if (!drawer) {
      return
    }

    drawer
      .querySelectorAll('[data-panel-toggle^="mobile-"]')
      .forEach(toggle => {
        const isOpen = toggle.dataset.panelToggle === this.#state.openPanel
        const facet = toggle.closest('.main-collection__facet')
        const panel = facet?.querySelector('.main-collection__facet-panel')

        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
        panel?.style.setProperty(
          '--mobile-panel-height',
          `${panel.scrollHeight}px`
        )
        facet?.classList.toggle('is-active', isOpen)
        panel?.classList.toggle('is-open', isOpen)
      })
  }

  #schedulePanelCloseCleanup(panelId) {
    this.#panelAnimationTimer = window.setTimeout(() => {
      if (this.#closingPanel !== panelId) {
        return
      }

      this.#closingPanel = null
      this.#syncDesktopPanels([panelId])
    }, PANEL_ANIMATION_MS)
  }

  #schedulePanelOpenCleanup(panelId) {
    this.#panelOpenAnimationTimer = window.setTimeout(() => {
      if (this.#openingPanel !== panelId) {
        return
      }

      this.#openingPanel = null
      this.#syncDesktopPanels([panelId])
    }, PANEL_ANIMATION_MS)
  }

  #syncDesktopPanels(panelIds) {
    ;[...new Set(panelIds.filter(Boolean))].forEach(panelId => {
      const toggle = this.#panelToggle(panelId)
      const panel = this.#panelForToggle(toggle)
      const panelState = this.#panelState(panelId, {
        isOpen: this.#state.openPanel === panelId,
      })

      if (!toggle || !panel) {
        return
      }

      toggle.setAttribute('aria-expanded', panelState.isOpen ? 'true' : 'false')
      panel.hidden = !panelState.isVisible
      panel.classList.remove('is-open', 'is-opening', 'is-closing')

      if (panelState.className) {
        panel.classList.add(panelState.className)
      }
    })
    this.#positionOpenPanels()
  }

  #panelToggle(panelId) {
    return [
      ...this.querySelectorAll(
        '[data-main-collection-toolbar] [data-panel-toggle]'
      ),
    ].find(toggle => toggle.dataset.panelToggle === panelId)
  }

  #panelForToggle(toggle) {
    return toggle
      ?.closest('.main-collection__facet')
      ?.querySelector(
        '.main-collection__facet-panel, .main-collection__sort-panel, .main-collection__page-size-panel'
      )
  }

  #toggleFacet(attribute, value) {
    this.#openingPanel = null
    const values = this.#state.facets[attribute] || []

    if (values.includes(value)) {
      this.#state.facets[attribute] = values.filter(item => item !== value)
    } else {
      this.#state.facets[attribute] = [...values, value]
    }

    if (!this.#state.facets[attribute].length) {
      delete this.#state.facets[attribute]
    }

    this.#state.page = 0
    this.#search({ updateUrl: true })
  }

  #syncRangeControls(input, options = {}) {
    const { commit = false } = options
    const slider = input.closest('[data-range-slider]')

    if (!slider?.dataset.rangeAttribute) {
      return
    }

    const attribute = slider.dataset.rangeAttribute
    const minAllowed = Number(slider.dataset.rangeMin || 0)
    const maxAllowed = Number(slider.dataset.rangeMax || 0)
    const minRange = slider.querySelector(
      '[data-range-input][data-range-side="min"]'
    )
    const maxRange = slider.querySelector(
      '[data-range-input][data-range-side="max"]'
    )
    const minText = slider.querySelector(
      '[data-range-text][data-range-side="min"]'
    )
    const maxText = slider.querySelector(
      '[data-range-text][data-range-side="max"]'
    )
    const side = input.dataset.rangeSide
    let min = Number(minRange?.value || minAllowed)
    let max = Number(maxRange?.value || maxAllowed)
    const rawValue = input.value.trim()

    if (!commit && input.matches('[data-range-text]') && rawValue === '') {
      return
    }

    const inputValue = Number(rawValue)

    if (Number.isFinite(inputValue)) {
      if (side === 'min') {
        min = clamp(inputValue, minAllowed, max)
      } else {
        max = clamp(inputValue, min, maxAllowed)
      }
    }

    min = Math.round(clamp(min, minAllowed, maxAllowed))
    max = Math.round(clamp(max, minAllowed, maxAllowed))

    if (min > max) {
      if (side === 'min') {
        min = max
      } else {
        max = min
      }
    }

    const bounds = {
      min: minAllowed,
      max: maxAllowed,
    }

    if (minRange) {
      minRange.value = String(min)
    }

    if (maxRange) {
      maxRange.value = String(max)
    }

    if (minText) {
      minText.value = String(min)
      minText.max = String(max)
    }

    if (maxText) {
      maxText.value = String(max)
      maxText.min = String(min)
    }

    slider.style.setProperty('--range-start', `${rangePercent(min, bounds)}%`)
    slider.style.setProperty('--range-end', `${rangePercent(max, bounds)}%`)

    if (!commit) {
      return
    }

    this.#openingPanel = null
    this.#state.ranges[attribute] = {
      min: String(min),
      max: String(max),
    }
    this.#state.page = 0
    this.#search({ updateUrl: true })
  }

  #applyRange(attribute) {
    this.#openingPanel = null
    const minInput = this.querySelector(
      `[data-range-input][data-range-attribute="${attribute}"][data-range-side="min"]`
    )
    const maxInput = this.querySelector(
      `[data-range-input][data-range-attribute="${attribute}"][data-range-side="max"]`
    )

    this.#state.ranges[attribute] = {
      min: minInput?.value || '',
      max: maxInput?.value || '',
    }
    this.#state.page = 0
    this.#search({ updateUrl: true })
  }

  #clearRange(attribute) {
    this.#openingPanel = null
    delete this.#state.ranges[attribute]
    this.#state.page = 0
    this.#search({ updateUrl: true })
  }

  #removeActiveFilter(button) {
    const attribute = button.dataset.filterAttribute
    const value = button.dataset.filterValue
    const rangeSide = button.dataset.filterRangeSide

    if (!attribute) {
      return
    }

    if (rangeSide && this.#state.ranges[attribute]) {
      delete this.#state.ranges[attribute][rangeSide]

      if (
        !this.#state.ranges[attribute].min &&
        !this.#state.ranges[attribute].max
      ) {
        delete this.#state.ranges[attribute]
      }
    } else if (value && this.#state.facets[attribute]) {
      this.#state.facets[attribute] = this.#state.facets[attribute].filter(
        item => item !== value
      )

      if (!this.#state.facets[attribute].length) {
        delete this.#state.facets[attribute]
      }
    } else {
      delete this.#state.facets[attribute]
      delete this.#state.ranges[attribute]
    }

    this.#state.page = 0
    this.#search({ updateUrl: true })
  }

  #openDrawer() {
    const drawer = this.querySelector('[data-main-collection-drawer]')

    if (drawer) {
      window.clearTimeout(this.#drawerAnimationTimer)
      this.#state.openPanel = null
      this.#renderDrawer(this.#lastFacetResponse || this.#lastResponse)
      drawer.classList.remove('is-closing')
      document.body.style.overflow = 'hidden'

      if (typeof drawer.showPopover === 'function') {
        if (!drawer.matches(':popover-open')) {
          drawer.showPopover()
        }
      } else {
        drawer.classList.add('is-open')
      }
    }
  }

  #isDrawerOpen() {
    const drawer = this.querySelector('[data-main-collection-drawer]')

    return Boolean(
      drawer?.matches(':popover-open') ||
      drawer?.classList.contains('is-open') ||
      drawer?.classList.contains('is-closing')
    )
  }

  #closeDrawer() {
    const drawer = this.querySelector('[data-main-collection-drawer]')

    if (drawer) {
      window.clearTimeout(this.#drawerAnimationTimer)

      if (typeof drawer.hidePopover === 'function') {
        if (drawer.matches(':popover-open')) {
          drawer.hidePopover()
        }

        document.body.style.overflow = ''
        return
      }

      drawer.classList.remove('is-open')
      drawer.classList.add('is-closing')
      this.#drawerAnimationTimer = window.setTimeout(() => {
        drawer.classList.remove('is-closing')
        document.body.style.overflow = ''
      }, DRAWER_ANIMATION_MS)
    }
  }

  #setLoading(isLoading) {
    this.toggleAttribute('aria-busy', isLoading)
  }

  #resolvedFilterOptions(includeConditional = false) {
    return resolveFilterOptions({
      filterOptions: this.#config.algolia.filterOptions || [],
      includeFilter: filter => {
        if (filter.value === CATEGORY_FILTER_ATTRIBUTE) {
          return includeConditional || this.#hasCategorySelection()
        }

        return Boolean(filter.value)
      },
      marketHandle: this.#config.market.handle,
    })
  }

  #hasCategorySelection() {
    return Boolean(this.#state.facets[CATEGORY_PARENT_FILTER_ATTRIBUTE]?.length)
  }

  #urlKey(filter) {
    return filter.label.toLowerCase()
  }

  #cleanValue(value, attribute) {
    if (this.#isColourAttribute(attribute)) {
      return String(value)
    }

    return String(value).replace(/ /g, '-')
  }

  #unCleanValue(value, attribute) {
    let decodedValue = String(value)

    try {
      decodedValue = decodeURIComponent(decodedValue.replace(/\+/g, ' '))
    } catch {
      decodedValue = String(value)
    }

    if (this.#isColourAttribute(attribute)) {
      return decodedValue
    }

    const parts = decodedValue.split('-')

    if (parts.length <= 1) {
      return decodedValue
    }

    const trimmedParts = parts.map(part => part.trim())
    const allPartsAreWords = trimmedParts.every(
      part =>
        part.length > 0 && !part.includes(' ') && /^[a-zA-Z0-9'&/]+$/.test(part)
    )

    if (!allPartsAreWords) {
      return decodedValue
    }

    let result = trimmedParts[0]

    for (let index = 1; index < trimmedParts.length; index += 1) {
      const previous = trimmedParts[index - 1]
      const current = trimmedParts[index]
      const shouldKeepHyphen =
        /^[A-Za-z]$/.test(previous) && /^[A-Za-z]/.test(current)
      const separator = shouldKeepHyphen ? '-' : ' '

      result += `${separator}${current}`
    }

    return result
  }

  #formatFacetLabel(attribute, value) {
    if (this.#isColourAttribute(attribute)) {
      const parts = String(value).split('-')

      if (parts.length > 2) {
        return `${parts[0]}/${parts[2]}`
      }

      return parts[0] || value
    }

    if (isPriceAttribute(attribute)) {
      return this.#formatMoneyWithoutDecimals(value)
    }

    return value
  }

  #isColourAttribute(attribute) {
    const normalized = attribute.toLowerCase()
    
    if (normalized.includes('metafields.custom.colour')) {
      return false
    }
    return normalized.includes('colour') || normalized === 'color'
  }

  #renderFacetSwatch(value) {
    const parts = String(value).split('-')
    const color = parts[1] || '#ffffff'
    const colorTwo = parts[3] || ''

    return `<span class="main-collection__swatch" style="--swatch-color: ${this.#escapeHtml(color)}; ${colorTwo ? `background: linear-gradient(90deg, ${this.#escapeHtml(color)} 50%, ${this.#escapeHtml(colorTwo)} 50%)` : ''}"></span>`
  }

  #mapContentBanners(userData) {
    const banners = new Map()

    ;(Array.isArray(userData) ? userData : []).forEach(banner => {
      const layout =
        banner[`${this.#columnCount()}-grid-layout`] ||
        banner['4-grid-layout'] ||
        {}
      const position = Number(layout.position || banner.position || 0)

      if (position > 0) {
        banners.set(position, banner)
      }
    })

    return banners
  }

  #visiblePages(currentPage, totalPages) {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, index) => index)
    }

    if (currentPage < 3) {
      return [0, 1, 2, 3, null, totalPages - 1]
    }

    if (currentPage > totalPages - 4) {
      return [
        0,
        null,
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
      ]
    }

    return [
      0,
      null,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      null,
      totalPages - 1,
    ]
  }

  #sendClickEvent(link) {
    void trackAlgoliaProductClick(this.#config, this.#state.sortIndex, link)
  }

  #sendConversionEvent(button, context = {}) {
    const product = context.product || {}
    const variant = context.variant || {}

    return trackAlgoliaProductConversion(
      this.#config,
      this.#state.sortIndex,
      button,
      {
        currency: this.#config.market.currencyCode,
        price: variant.price || product.price?.amount,
        quantity: context.quantity || 1,
        queryID: product.__queryID || button?.dataset.queryId,
      }
    )
  }

  #handleHiddenSale() {
    if (
      this.#config.hiddenSale?.templateSuffix !== 'sale' ||
      !location.search.includes('hidden-sale=true')
    ) {
      return
    }

    const expires = new Date()
    const cookieExpiry = Number(this.#config.hiddenSale.cookieExpiry)

    expires.setTime(expires.getTime() + (Number.isFinite(cookieExpiry) ? cookieExpiry : 96) * 60 * 60 * 1000)
    document.cookie = `${HIDDEN_SALE_COOKIE}=true;expires=${expires.toUTCString()};path=/`
  }

  #cardProduct(card) {
    const script = card.querySelector(
      '[data-card-product], [data-product-card-json]'
    )

    return this.#parseJson(script?.textContent)
  }

  #parseJson(value) {
    return parseJson(value)
  }

  #formatMoney(value) {
    return formatMoney(value, {
      currencySymbol: this.#config.market.currencySymbol,
      formatter: this.#moneyFormatter,
    })
  }

  #formatMoneyWithoutDecimals(value) {
    const amount = Math.round(Number(value || 0))

    try {
      return new Intl.NumberFormat(this.#config.market.language || undefined, {
        style: 'currency',
        currency: this.#config.market.currencyCode,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(amount)
    } catch {
      return `${this.#config.market.currencySymbol}${amount}`
    }
  }

  #createMoneyFormatter() {
    return createMoneyFormatter(this.#config.market)
  }

  #sizeSort(a, b) {
    const order = [
      'XS',
      'S',
      'M',
      'L',
      'XL',
      'XXL',
      '1XL',
      '2XL',
      '3XL',
      '4XL',
      '5XL',
      '1SZ',
      'M/L',
      'L/XL',
      '3',
      '4',
      '5',
      '6',
      '7',
      '7.5',
      '8',
      '8.5',
      '9',
      '9.5',
      '10',
      '10.5',
      '11',
      '12',
      '13',
      '14',
      '28',
      '29',
      '30',
      '31',
      '32',
      '33',
      '34',
      '36',
      '38',
      '40',
      '28 / 30',
      '28 / 32',
      '30 / 30',
      '30 / 32',
      '30 / 34',
      '32 / 30',
      '32 / 32',
      '32 / 34',
      '34 / 30',
      '34 / 32',
      '34 / 34',
      '36 / 30',
      '36 / 32',
      '36 / 34',
      '38 / 32',
      '0-3 M',
      '3-6 M',
      '6-9 M',
      '9-12 M',
      '3/4 Y',
      '5/6 Y',
      '7/8 Y',
      '8/9 Y',
      '9/10 Y',
      '10/11 Y',
      '12/13 Y',
      '14/15 Y',
      '15/16 Y',
    ]

    const aIndex = order.indexOf(a)
    const bIndex = order.indexOf(b)

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex
    }

    if (aIndex !== -1) {
      return -1
    }

    if (bIndex !== -1) {
      return 1
    }

    return String(a).localeCompare(String(b), undefined, { numeric: true })
  }

  #columnCount() {
    if (this.#isMobile()) {
      return 2
    }

    return Number(localStorage.getItem('--column-count')) || 4
  }

  #isMobile() {
    return window.matchMedia('(width < 1024px)').matches
  }

  #alignmentValue(value) {
    const mapping = {
      bottom: 'flex-end',
      center: 'center',
      left: 'flex-start',
      right: 'flex-end',
      top: 'flex-start',
    }

    return mapping[value] || value
  }

  #imageUrl(image, width) {
    return imageUrl(image, width)
  }

  #handleize(value) {
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  #escapeHtml(value) {
    return escapeHtml(value)
  }

  #icon(name) {
    return this.#iconMarkup[name] || ''
  }
}

if (!customElements.get('lyle-main-collection')) {
  customElements.define('lyle-main-collection', LyleMainCollection)
}
